import React, { useState, useEffect, useCallback } from "react";
import {
  Box, FormControl, FormLabel, Input, Text, Heading, Button,
  useToast, useColorModeValue, Flex, VStack, HStack, Textarea, Switch, Badge,
} from "@chakra-ui/react";
import axios from "axios";
import { MasterPromotion } from '../../Platform/Common/types';

const emptyPromotion: MasterPromotion = {
  promotion_id: 0,
  headline: '',
  text: '',
  promo_coupon_code: '',
  is_active: false,
  created_at: new Date(),
  updated_at: new Date(),
};

const B2CReviewMasterPromotion = () => {
  const cardbg = useColorModeValue("gray.50", "gray.700");
  const toast = useToast();

  const [promotion, setPromotion] = useState<MasterPromotion>(emptyPromotion);
  const [isEditing, setIsEditing] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const isCreating = promotion.promotion_id === 0;
  const showSaveButton = isCreating || isEditing;

  const sectionBg = useColorModeValue("gray.100", "gray.700");
  const textBg = useColorModeValue("gray.600", "gray.400");

  const fetchPromotion = useCallback(async () => {
    try {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<MasterPromotion>(`${b2c_apiurl}/master-promotion`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setPromotion(data || emptyPromotion);
    } catch (err: any) {
      if (err?.response?.status !== 404) console.error(err);
      setPromotion(emptyPromotion); // 404 just means none created yet
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchPromotion(); }, [fetchPromotion]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPromotion(prev => ({ ...prev, [name]: value }));
    setIsEdited(true);
  };

  const handleCreate = async () => {
    try {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.post<MasterPromotion>(`${b2c_apiurl}/master-promotion`, promotion, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setPromotion(data);
      setIsEditing(false);
      setIsEdited(false);
      toast({ title: "Master promotion created", status: "success" });
    } catch (err: any) {
      console.error(err);
      toast({ title: err?.response?.data?.detail || "Error creating promotion", status: "error" });
    }
  };

  const handleUpdate = async () => {
    try {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      await axios.put(`${b2c_apiurl}/master-promotion/${promotion.promotion_id}`, promotion, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      toast({ title: "Master promotion updated", status: "success" });
      setIsEditing(false);
      setIsEdited(false);
      fetchPromotion();
    } catch (err: any) {
      console.error(err);
      toast({ title: err?.response?.data?.detail || "Error updating promotion", status: "error" });
    }
  };

  const handleSave = () => (isCreating ? handleCreate() : handleUpdate());

  const handleCancel = () => {
    setIsEditing(false);
    setIsEdited(false);
    fetchPromotion();
  };

  // Flipping active/inactive is the routine action here -- kept separate
  // from "Edit Content" so admins don't have to enter edit mode for it.
  const handleToggleActive = async () => {
    try {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const updated = { ...promotion, is_active: !promotion.is_active };
      await axios.put(`${b2c_apiurl}/master-promotion/${promotion.promotion_id}`, updated, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setPromotion(updated);
      toast({ title: updated.is_active ? "Master promotion activated" : "Master promotion deactivated", status: "success" });
    } catch (err: any) {
      console.error(err);
      toast({ title: err?.response?.data?.detail || "Error updating status", status: "error" });
    }
  };

  if (!loaded) return null;

  return (
    <Box as="main" mx="auto" p={10}>
      <Box bg={sectionBg} p={4} borderRadius="sm" mb={4}>
        <Flex justify="space-between" align="center" wrap="wrap">
          <Heading size="lg">
            {isCreating ? "Master Promotion (Not Created Yet)" : promotion.headline}
          </Heading>
          {!isCreating && (
            <Badge colorScheme={promotion.is_active ? "green" : "gray"} fontSize="0.8em" px={3} py={1}>
              {promotion.is_active ? "Active" : "Inactive"}
            </Badge>
          )}
        </Flex>
        {!isCreating && (
          <Text mt={2} fontSize="sm" color={textBg}>
            Only one master promotion exists platform-wide. Toggle it on or off below, or edit its content.
          </Text>
        )}
      </Box>

      {!isCreating && (
        <HStack spacing={2} mb={4}>
          <Button size="sm" colorScheme={promotion.is_active ? "orange" : "green"} onClick={handleToggleActive} isDisabled={isEditing}>
            {promotion.is_active ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" colorScheme="blue" onClick={() => setIsEditing(true)} isDisabled={isEditing}>
            Edit Content
          </Button>
          {isEditing && (
            <Button size="sm" variant="outline" colorScheme="red" onClick={handleCancel}>Cancel</Button>
          )}
        </HStack>
      )}

      <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
        <fieldset disabled={!showSaveButton}>
          <Text mb={4} fontSize="md" fontWeight="bold">Promotion Details</Text>
          <VStack spacing={4} align="stretch">
            <FormControl id="headline" isRequired>
              <Input name="headline" placeholder="Headline*" size="sm" value={promotion.headline} onChange={handleInputChange} />
            </FormControl>
            <FormControl id="text">
              <Textarea name="text" placeholder="Promotion Text" size="sm" value={promotion.text || ""} onChange={handleInputChange} />
            </FormControl>
            <FormControl id="promo_coupon_code">
              <Input name="promo_coupon_code" placeholder="Coupon Code" size="sm" value={promotion.promo_coupon_code || ""} onChange={handleInputChange} />
            </FormControl>
            {isCreating && (
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="is_active" mb="0" fontSize="sm">Activate immediately</FormLabel>
                <Switch id="is_active" isChecked={promotion.is_active} onChange={(e) => { setPromotion(prev => ({ ...prev, is_active: e.target.checked })); setIsEdited(true); }} />
              </FormControl>
            )}
          </VStack>
        </fieldset>
      </Box>

      {showSaveButton && (
        <Box textAlign="left" mt={4}>
          <Button colorScheme="teal" size="sm" borderRadius="sm" onClick={handleSave} isDisabled={isCreating ? !promotion.headline : !isEdited}>
            {isCreating ? "Create Master Promotion" : "Save Changes"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default B2CReviewMasterPromotion;